import { useCallback, useEffect, useState } from "react";
import { apiFetch } from "../api/setupAxios";

// Activación de las notificaciones push del móvil (mismo sistema que el botón de
// la supervisora: service worker + suscripción VAPID + registro en /device-notifs/).
// Cada dispositivo se registra por separado (device_id en localStorage).

export const esIOS = () =>
  /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
export const esPWAInstalada = () =>
  window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;

export const instruccionesBloqueadas = () => {
  if (esIOS() && !esPWAInstalada()) {
    return "En iPhone/iPad primero hay que instalar la app:\n1. Toca el icono compartir 📤 en Safari\n2. «Añadir a pantalla de inicio»\n3. Abre la app desde ese icono y actívalas desde ahí";
  }
  if (esPWAInstalada()) {
    return "Para desbloquearlas:\n1. Mantén pulsado el icono de la app\n2. Info de la app → Permisos → Notificaciones → Permitir";
  }
  return "Para desbloquearlas en Chrome:\n1. Toca el candado 🔒 de la barra de dirección\n2. Configuración del sitio → Notificaciones → Permitir";
};

// Mensaje cuando el navegador no consigue registrarse en su servicio de avisos
// ("Registration failed - push service error"): casi siempre es cosa del navegador.
const esBrave = () => !!(navigator.brave && navigator.brave.isBrave);
export const ayudaFalloRegistro = () => {
  if (esBrave()) {
    return "Tu navegador (Brave) tiene desactivado el servicio de avisos.\nAbre brave://settings/privacy, activa «Usar los servicios de Google para la mensajería push», cierra y vuelve a abrir el navegador y pulsa de nuevo «Activar».";
  }
  return "Tu navegador no ha podido registrarse en el servicio de avisos. Prueba esto y vuelve a pulsar «Activar»:\n" +
    "1. Usa Google Chrome (no ventana de incógnito).\n" +
    "2. Chrome → ⋮ → Configuración → Configuración de sitios → busca esta web → «Borrar y restablecer».\n" +
    "3. Si tienes la app instalada en el móvil, ábrela desde su icono.\n" +
    "Si sigue sin funcionar, avisa indicando qué móvil y navegador usas.";
};

// Datos del móvil para el panel de Notificaciones de superadmin (modelo, sistema,
// navegador, si la app está instalada). En iPhone el navegador no da el modelo.
export async function infoDispositivo() {
  const info = { instalada: esPWAInstalada() };
  const ua = navigator.userAgent || "";
  try {
    const h = await navigator.userAgentData.getHighEntropyValues(["model", "platform", "platformVersion", "fullVersionList"]);
    info.modelo = h.model || null;
    if (h.platform) info.sistema = `${h.platform}${h.platformVersion ? " " + h.platformVersion.split(".")[0] : ""}`;
    const b = (h.fullVersionList || []).find((x) => !/not.?a.?brand|chromium/i.test(x.brand));
    if (b) info.navegador = `${b.brand} ${String(b.version).split(".")[0]}`;
  } catch (_) {}
  if (!info.sistema) {
    const m = ua.match(/(?:iPhone OS|CPU OS) (\d+)/);
    if (m) { info.sistema = `iOS ${m[1]}`; info.modelo = info.modelo || (/iPad/.test(ua) ? "iPad" : "iPhone"); }
    else if (/Android (\d+)/.test(ua)) info.sistema = `Android ${ua.match(/Android (\d+)/)[1]}`;
  }
  if (!info.navegador) {
    const m = ua.match(/(CriOS|FxiOS|EdgA|Edg|Firefox|SamsungBrowser|Version)\/(\d+)/);
    if (m) info.navegador = `${({ CriOS: "Chrome", FxiOS: "Firefox", EdgA: "Edge", Edg: "Edge", SamsungBrowser: "Samsung Internet", Version: "Safari" })[m[1]] || m[1]} ${m[2]}`;
  }
  return info;
}

export const getDeviceId = () => {
  try {
    const tok = JSON.parse(localStorage.getItem("session_token") || "null");
    if (tok && tok.device_id) return String(tok.device_id);
    let id = localStorage.getItem("device_id");
    if (!id) {
      id = (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : Math.random().toString(36).slice(2, 10);
      localStorage.setItem("device_id", id);
    }
    return id;
  } catch (_) {
    return "unknown-device";
  }
};

const urlBase64ToUint8Array = (b64) => {
  const pad = "=".repeat((4 - (b64.length % 4)) % 4);
  const raw = window.atob((b64 + pad).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
};

const mismaClave = (sub, publicKey) => {
  try {
    const k = sub.options && sub.options.applicationServerKey;
    if (!k) return true; // el navegador no la expone: se asume la nuestra
    const a = new Uint8Array(k), b = urlBase64ToUint8Array(publicKey);
    return a.length === b.length && a.every((x, i) => x === b[i]);
  } catch (_) {
    return true;
  }
};

// Los avisos van en un service worker PROPIO (/push-sw.js, ámbito /avisos/), no en
// el /sw.js de la raíz: en algunos Android (Xiaomi HyperOS) suscribirse desde el de
// la raíz fallaba siempre ("Registration failed - push service error") y desde uno
// en su propio ámbito funciona (comprobado en el móvil).
const PUSH_SW = "/push-sw.js";
const PUSH_SCOPE = "/avisos/";

const registroAvisos = async () => {
  const reg = await navigator.serviceWorker.register(PUSH_SW, { scope: PUSH_SCOPE });
  if (!reg.active) {
    await new Promise((ok) => {
      const w = reg.installing || reg.waiting;
      if (!w) return ok();
      w.addEventListener("statechange", () => { if (w.state === "activated") ok(); });
      setTimeout(ok, 8000);
    });
  }
  return reg;
};

// Suscripción vigente en este navegador: la del service worker de avisos o, si no
// hay, la antigua del /sw.js de la raíz (los que ya las activaron antes siguen
// funcionando con esa y no hay que tocarla).
const suscripcionActual = async () => {
  try {
    const regs = await navigator.serviceWorker.getRegistrations();
    const avisos = regs.find((x) => x.scope.endsWith(PUSH_SCOPE));
    const raiz = regs.find((x) => new URL(x.scope).pathname === "/");
    for (const reg of [avisos, raiz]) {
      const sub = reg && reg.pushManager ? await reg.pushManager.getSubscription() : null;
      if (sub) return sub;
    }
  } catch (_) {}
  return null;
};

export const pushSoportado = () => "Notification" in window && "serviceWorker" in navigator && typeof window.PushManager !== "undefined";

// ¿Está este dispositivo registrado y activado en el servidor?
export async function estadoPush(user) {
  if (!pushSoportado()) return esIOS() && !esPWAInstalada() ? "instalar" : "no_soportado";
  if (Notification.permission === "denied") return "bloqueado";
  try {
    const r = await apiFetch("/device-notifs/devices?user=" + encodeURIComponent(user || ""), { credentials: "include" });
    if (!r.ok) return "desconocido"; // no se ha podido comprobar: no se enseña "Activar"
    const j = await r.json();
    const me = (j.devices || []).find((d) => String(d.device_id) === getDeviceId());
    if (me && !me.enabled) return "desactivado"; // las quitó a propósito: no insistir
    if (!me || Notification.permission !== "granted") return "inactivo";
    // El servidor la tiene activa, pero ¿el navegador sigue teniendo suscripción?
    // (otro usuario o el sistema pueden haberla anulado). Si no la tiene → inactivo
    // (se reintenta sola). Si la tiene, se reenvía al servidor una vez por sesión
    // para que guarde la vigente.
    const sub = await suscripcionActual();
    if (!sub) return "inactivo";
    const marca = "push_sync_" + user + "_" + sub.endpoint.slice(-24);
    // enabled=2: "relevada" porque en este móvil entró otra persona → al volver a
    // abrir la app se reactiva siempre (y releva a la otra)
    const relevada = me.enabled === 2;
    try {
      if (relevada || !sessionStorage.getItem(marca)) {
        const res = await apiFetch("/device-notifs/", {
          method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ user, device_id: getDeviceId(), subscription: sub.toJSON ? sub.toJSON() : sub, enabled: true, info: await infoDispositivo() }),
        });
        if (res.ok) sessionStorage.setItem(marca, "1");
      }
    } catch (_) {}
    return "activo";
  } catch (_) {
    return "desconocido"; // sin conexión: no se sabe, no se enseña "Activar"
  }
}

// Pide permiso, suscribe este dispositivo y lo registra. Devuelve {ok, error}.
// Debe llamarse directamente desde el clic (si no, Chrome no enseña el permiso).
// Nunca dos activaciones a la vez: al cerrar el diálogo de permiso la pantalla vuelve
// a primer plano y el reintento automático arrancaba otra suscripción en paralelo con
// la del botón (dos peticiones simultáneas a Play Services → las dos fallaban).
let activacionEnCurso = null;
export function activarPush(user) {
  if (!activacionEnCurso) activacionEnCurso = activarPushUnaVez(user).finally(() => { activacionEnCurso = null; });
  return activacionEnCurso;
}

async function activarPushUnaVez(user) {
  if (!pushSoportado()) return { ok: false, error: esIOS() && !esPWAInstalada() ? instruccionesBloqueadas() : "Este navegador no permite notificaciones." };
  let perm = Notification.permission;
  if (perm !== "granted") {
    try { perm = await Notification.requestPermission(); } catch (_) {}
  }
  if (perm !== "granted") return { ok: false, error: "Notificaciones bloqueadas.\n\n" + instruccionesBloqueadas() };
  try {
    const reg = await registroAvisos();
    const r = await fetch("/device-notifs/vapid_public");
    const { publicKey } = await r.json();
    if (!publicKey) return { ok: false, error: "No se pudo conectar con el servidor de avisos." };
    // El navegador tiene UNA suscripción por dispositivo, compartida por todos los
    // usuarios que entran en él. Antes se anulaba siempre al activar: si en el mismo
    // móvil entraba otro usuario, se cargaba la suscripción del primero y a ese ya no
    // le llegaba nada (aunque Google siguiera aceptando los envíos un tiempo).
    // Ahora se reutiliza si es de nuestra clave; solo se anula si es de otra clave.
    let sub = null;
    try {
      const prev = await suscripcionActual();
      if (prev) {
        if (mismaClave(prev, publicKey)) sub = prev;
        else await prev.unsubscribe();
      }
    } catch (_) {}
    const subscribe = () => reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(publicKey) });
    if (!sub) {
      // Un solo intento (y un único reintento a los 3 s si el servicio de avisos del
      // móvil responde con error). NO se anula nada ni se desregistra el service
      // worker: cada anulación/registro nuevo cuenta para Google Play Services, y
      // muchos seguidos hacen que el móvil rechace los siguientes durante un tiempo.
      try { sub = await subscribe(); } catch (e) {
        if (!e || e.name !== "AbortError") throw e;
        await new Promise((ok) => setTimeout(ok, 3000));
        try { sub = await subscribe(); } catch (e2) {
          const err = new Error(ayudaFalloRegistro());
          err.name = "FalloRegistro";
          err.original = e2 && (e2.message || e2.name);
          throw err;
        }
      }
    }
    const device_id = getDeviceId();
    const res = await apiFetch("/device-notifs/", {
      method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ user, device_id, subscription: sub.toJSON ? sub.toJSON() : sub, enabled: true, info: await infoDispositivo() }),
    });
    const jr = await res.json().catch(() => ({}));
    if (!res.ok || !jr.ok) return { ok: false, error: "No se pudo guardar: " + (jr.error || res.status) };
    try { localStorage.setItem("session_token", JSON.stringify({ user: String(user), device_id })); } catch (_) {}
    window.dispatchEvent(new Event("push-estado"));
    return { ok: true };
  } catch (e) {
    try {
      // Datos del móvil para poder diagnosticar sin preguntar (modelo, Android, Chrome, red)
      let info = {};
      try {
        const h = await navigator.userAgentData.getHighEntropyValues(["model", "platformVersion", "fullVersionList"]);
        info = { modelo: h.model, android: h.platformVersion, navegador: (h.fullVersionList || []).map((x) => x.brand + " " + x.version).join(", ") };
      } catch (_) {}
      try { info.red = navigator.connection ? navigator.connection.type + "/" + navigator.connection.effectiveType : "?"; } catch (_) {}
      info.instalada = esPWAInstalada();
      info.permiso = typeof Notification !== "undefined" ? Notification.permission : "?";
      fetch("/device-notifs/log-error", { method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user, device_id: getDeviceId(), step: "activarPush",
          error: String(e && (e.original || e.message || e.name)) + " | " + JSON.stringify(info), ua: navigator.userAgent }) });
    } catch (_) {}
    if (e && e.name === "NotAllowedError") return { ok: false, error: instruccionesBloqueadas() };
    if (e && e.name === "FalloRegistro") return { ok: false, fallo_movil: true, error: e.message };
    if (e && e.name === "AbortError") return { ok: false, fallo_movil: true, error: ayudaFalloRegistro() };
    return { ok: false, error: "No se pudieron activar: " + (e && (e.message || e.name)) };
  }
}

// Desactiva los avisos al móvil en ESTE dispositivo (enabled=0 en el servidor; se
// conserva la suscripción para poder volver a activarlas). Devuelve {ok, error}.
export async function desactivarPush(user) {
  try {
    let subJson = {};
    const sub = await suscripcionActual();
    if (sub) subJson = sub.toJSON ? sub.toJSON() : sub;
    const res = await apiFetch("/device-notifs/", {
      method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ user, device_id: getDeviceId(), subscription: subJson, enabled: false }),
    });
    const jr = await res.json().catch(() => ({}));
    if (!res.ok || !jr.ok) return { ok: false, error: "No se pudo guardar: " + (jr.error || res.status) };
    window.dispatchEvent(new Event("push-estado"));
    return { ok: true };
  } catch (e) {
    return { ok: false, error: "No se pudieron desactivar: " + (e && (e.message || e.name)) };
  }
}

// Hook: estado de este dispositivo + función para activarlo (se sincroniza entre
// componentes con el evento "push-estado").
// Si el permiso ya está concedido pero el móvil no consiguió registrarse (fallo del
// servicio de avisos del propio móvil), se reintenta solo, sin molestar: al abrir la
// app, al volver a ella y al recuperar conexión (como mucho cada 2 minutos).
// Reintentos automáticos espaciados (5 min, 15 min, 45 min… hasta 6 h): cuando el
// servicio de avisos del móvil falla suele ser algo temporal, y reintentar muy seguido
// hace que lo siga rechazando.
const CLAVE_REINTENTO = "push_reintento_auto"; // {n, siguiente}
let intentoEnCurso = null;
const reintentoSilencioso = async (user) => {
  if (!user || !pushSoportado() || Notification.permission !== "granted") return false;
  let est = { n: 0, siguiente: 0 };
  try { est = JSON.parse(localStorage.getItem(CLAVE_REINTENTO) || "null") || est; } catch (_) {}
  if (Date.now() < est.siguiente) return false;
  if (!intentoEnCurso) intentoEnCurso = activarPush(user).finally(() => { intentoEnCurso = null; });
  const r = await intentoEnCurso;
  try {
    if (r.ok) localStorage.removeItem(CLAVE_REINTENTO);
    else {
      const espera = Math.min(5 * 60 * 1000 * Math.pow(3, est.n), 6 * 3600 * 1000);
      localStorage.setItem(CLAVE_REINTENTO, JSON.stringify({ n: est.n + 1, siguiente: Date.now() + espera }));
    }
  } catch (_) {}
  return r.ok;
};

export function usePush(user) {
  const [estado, setEstado] = useState("cargando");
  const [activando, setActivando] = useState(false);
  const [fallo, setFallo] = useState(false);
  const [exito, setExito] = useState(false); // recién activadas en esta pantalla → mensaje verde
  const refrescar = useCallback(() => {
    if (user) estadoPush(user).then((e) => { if (e !== "desconocido") setEstado(e); });
  }, [user]);
  useEffect(() => {
    if (!user) return undefined;
    let vivo = true;
    const comprobar = async () => {
      const e = await estadoPush(user);
      if (!vivo) return;
      // Sin poder comprobarlo (sin red, error del servidor) se deja lo que hubiera: si aún
      // no se sabía nada sigue oculto ("cargando"), nunca sale "Activar" por un fallo.
      if (e === "desconocido") return;
      if (e === "inactivo" && Notification.permission === "granted") {
        // Con permiso ya dado se intenta activar en silencio ANTES de enseñar "Activar":
        // si sale bien, el aviso no llega a aparecer.
        if (await reintentoSilencioso(user)) {
          if (vivo) { setFallo(false); setExito(true); refrescar(); }
          return;
        }
        if (!vivo) return;
      }
      setEstado(e);
    };
    const alVolver = () => { if (document.visibilityState === "visible") comprobar(); };
    comprobar();
    window.addEventListener("push-estado", refrescar);
    document.addEventListener("visibilitychange", alVolver);
    window.addEventListener("online", comprobar);
    return () => {
      vivo = false;
      window.removeEventListener("push-estado", refrescar);
      document.removeEventListener("visibilitychange", alVolver);
      window.removeEventListener("online", comprobar);
    };
  }, [user, refrescar]);
  const activar = async () => {
    setActivando(true);
    const r = await activarPush(user);
    setActivando(false);
    if (r.ok) { setFallo(false); setExito(true); try { localStorage.removeItem(CLAVE_REINTENTO); } catch (_) {} }
    else if (r.fallo_movil) setFallo(true); // sin instrucciones: se reintenta solo
    else window.alert(r.error);
    refrescar();
    return r.ok;
  };
  useEffect(() => {
    if (!exito) return undefined;
    const t = setTimeout(() => setExito(false), 10000);
    return () => clearTimeout(t);
  }, [exito]);
  const desactivar = async () => {
    const r = await desactivarPush(user);
    if (!r.ok) window.alert(r.error);
    setExito(false);
    refrescar();
    return r.ok;
  };
  return { estado, activar, desactivar, activando, fallo, exito };
}
